import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { DATA_TYPE } from '@constants/data-type.constant';
import { Signal } from '@models/dashboard/signal';
import { ChartSignalEntry } from '@models/pages/user-defined-page/chart-signal-entry';
import { CommandRequest } from '@models/pages/user-defined-page/command';
import { UserDefinedChartDetails } from '@models/pages/user-defined-page/user-defined-chart-details';
import { ModalService } from '@services/modal/modal.service';
import { NotificationService } from '@services/notification/notification.service';
import { UserDefinedPageService } from '@services/pages/user-defined-page/user-defined-page.service';
import { StatusEventSignalDataHelperService } from '@services/status-event-signal-data/status-event-signal-data-helper.service';
import { TranslationService } from '@services/translation/translation.service';
import { UtilsService } from '@services/utils/utils.service';
import { finalize, interval, Subject, Subscription, takeUntil, timer } from 'rxjs';
import { CommandContentModalComponent } from './command-content-modal/command-content-modal.component';

@Component({
    selector: 'scitis-command-content',
    templateUrl: './command-content.component.html',
    styleUrls: ['./command-content.component.scss'],
    standalone: false
})
export class CommandContentComponent implements OnInit, OnChanges {
  @Input()
  chartSettings: UserDefinedChartDetails | undefined;

  @Input()
  machineSignals: any = {};

  @Input()
  data: any = [];

  @Input()
  machineId: number = -1;

  @Input()
  userId: string = '';

  @Input()
  isInEditMode: boolean = false;

  @Input()
  editCommandEnabled: boolean = false;

  submitted: boolean = false;
  commandForm: UntypedFormGroup = new UntypedFormGroup({});
  signalsForm: UntypedFormGroup = new UntypedFormGroup({});
  isSendButtonDisabled: boolean = false;
  isSendingCommand: boolean = false;
  gateways = [];
  pollingSubscription: Subscription | undefined;

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private portalLanguage: string = '';
  private hasResult: boolean = false;
  private pollingSuccessful: boolean = false;


  private previousValuesMap: Map<number, any> = new Map<number, any>();

  constructor(
    private readonly utilsService: UtilsService,
    private readonly translationService: TranslationService,
    private readonly userDefinedPageService: UserDefinedPageService,
    private readonly statusEventSignalDataHelperService: StatusEventSignalDataHelperService,
    private readonly notification: NotificationService,
    private readonly modalService: ModalService,
  ) {}

  get f() {
    return this.commandForm.controls;
  }
  get f2() {
    return this.signalsForm.controls;
  }

  ngOnInit(): void {
    this.generateForm();
    this.userDefinedPageService.saveSuccess$.subscribe(res => {
      if (this.machineSignals) {
        this.generateSignalsForm();
      }

    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.editCommandEnabled) {
      if (changes.editCommandEnabled.currentValue) {
        this.f.username?.enable();
        this.f.password?.enable();
      } else {
        this.commandForm.reset();
        this.signalsForm.reset();
        this.f.username?.disable();
        this.f.password?.disable();
      }
    }
    if(changes?.machineSignals && changes?.machineSignals.currentValue) {
      this.generateSignalsForm();
    }
  }

  onSendCommand() {
    this.isSendingCommand = true;
    this.destroy$.next(true);
    this.pollingSuccessful = false;
    if (this.commandForm.invalid || this.signalsForm.invalid) {
      this.commandForm.markAllAsTouched();
      this.signalsForm.markAllAsTouched();
      this.isSendButtonDisabled = true;
      return;
    }
    const values: { field: string, value: string }[] = [];
    for(const key in this.signalsForm.controls) {
      if (this.signalsForm.controls.hasOwnProperty(key)) {
        const control = this.signalsForm.controls[key];
        if (control.value) {
          const machineSignal = (Object.values(this.machineSignals) as Signal[]).find((machineSignal: Signal) => machineSignal.name == key);
          values.push(
            {
              field: machineSignal?.signal ?? key,
              value: control.value
            }
          );
        }
      }
    }
    const request: CommandRequest = {
      credentials: {
        userName: this.f.username.value,
        password: this.f.password.value
      },
      values
    };
    if ( this.chartSettings?.gatewayId) {
      this.isSendButtonDisabled = true;
      this.userDefinedPageService.sendWritebackCommand(String(this.machineId), this.userId, this.chartSettings?.gatewayId, request).subscribe({
        next: res => {
          const pollingInterval = interval(5000);
          const pollingDuration = timer(30000);

          this.pollingSubscription = pollingInterval
            .pipe(takeUntil(pollingDuration))
            .pipe(takeUntil(this.destroy$))
            .pipe(
              finalize(() => {
                if (!this.pollingSuccessful) {
                  const modalRef = this.modalService.open(CommandContentModalComponent, {size: 'lg'});
                    modalRef.componentInstance.status = '-';
                    modalRef.componentInstance.payload = 'Polling timeout';
                }
                this.isSendButtonDisabled = false;
                this.isSendingCommand = false;
                this.unsubscribePolling();
              })
            )
            .subscribe(() => {
              this.userDefinedPageService.writebackResult(String(this.machineId), this.chartSettings!.gatewayId, res.correlationId).subscribe({
                next: (result: {status: number, payload: string}) => {
                  if (result?.status) {
                    this.pollingSuccessful = true;
                    const modalRef = this.modalService.open(CommandContentModalComponent, {size: 'lg'});
                    modalRef.componentInstance.status = result?.status;
                    modalRef.componentInstance.payload = result?.payload;
                    this.isSendingCommand = false;
                    this.unsubscribePolling();
                  }
                }
              });
            });
        },
        error: err => {
          console.error(err);
          this.isSendingCommand = false;
          this.isSendButtonDisabled = false;
          this.unsubscribePolling();
        }
      });
    }
  }

  getValueOrFallback(signal: ChartSignalEntry): string {
    const machineSignalById = this.getMachineSignalById(
      signal.signalId,
      this.machineSignals
    );
    const value: string | undefined = this.getValue(signal, machineSignalById);
    if (!value && !this.previousValuesMap.get(signal.signalId)) {
      return 'N/A';
    }
    if (!value) {
      return this.previousValuesMap.get(signal.signalId) + '';
    }
    return value.toString();
  }

  getValue(signal: ChartSignalEntry, machineSignalById: Signal | undefined): string | undefined {
    if (!this.isReceivedDataValid(this.data)) {
      return undefined;
    }
    let precision: number = signal.precision;
    let scale: number = signal.scale;

    if (machineSignalById) {
      precision = machineSignalById.decimals;
      scale = machineSignalById.scale;
    }

    const values = this.data[0];
    const filteredVs = values.vs.filter((m) => m.s === String(signal.signalId));
    if (filteredVs && filteredVs.length > 0) {
      const value = filteredVs[0].v;

      const jsonObject =
        this.statusEventSignalDataHelperService.tryParseJSONObject(value);
      if (jsonObject != null) {
        const enhancedValue =
          this.statusEventSignalDataHelperService.getEnhancedValue(
            this.portalLanguage,
            jsonObject
          );
        this.previousValuesMap.set(signal.signalId, enhancedValue);
        return enhancedValue;
      }
      const parsedValue = this.parseSignalValue(
        signal.signalType,
        value,
        precision,
        scale
      );
      this.previousValuesMap.set(signal.signalId, parsedValue);
      return parsedValue;
    }

    return undefined;
  }

  parseSignalValue(type, value, precision, scale: number) {
    return scale === 0 && (type === DATA_TYPE.INT16 || type === DATA_TYPE.INT32 || type === DATA_TYPE.INT64 || type === DATA_TYPE.INTEGER)
      ? Number(value).toLocaleString('fullwide', {useGrouping: false, maximumFractionDigits: 0})
      : this.utilsService.parseSignalValue(type, value, precision);
  }

  getLabelAndUnit(signal: ChartSignalEntry): string {
    const label: string = signal.displayedText;
    let unit: string = this.getUnit(signal);
    if (!unit || unit.isNullOrWhitespace()) {
      unit = '';
    } else {
      unit = ' [' + unit + ']';
    }
    const machineSignalById = this.getMachineSignalById(signal.signalId, this.machineSignals);
    if (!machineSignalById) {
      return this.translationService.translate.instant(label) + unit;
    }
    return this.translationService.translate.instant(machineSignalById.label) + unit;
  }

  getUnit(signal: ChartSignalEntry): string {
    let unit = '';
    if (signal.unit && signal.unit !== '-') {
      unit = signal.unit;
    }

    const machineSignalById = this.getMachineSignalById(signal.signalId, this.machineSignals);
    if (!machineSignalById) {
      return unit;
    }
    return machineSignalById.unit;
  }

  notWritable(signal: ChartSignalEntry): boolean {
    return !(Object.values(this.machineSignals) as Signal[]).find((machineSignal: Signal) => machineSignal.id == signal.signalId)?.writable;
  }

  minMaxValueString(signal: ChartSignalEntry): string {
    const machineSignal = (Object.values(this.machineSignals) as Signal[]).find((machineSignal: Signal) => machineSignal.id == signal.signalId);
    if (machineSignal?.writable && (machineSignal.minWritableValue != null && machineSignal.maxWritableValue != null)) {
      return `(Min: ${machineSignal.minWritableValue}, Max: ${machineSignal.maxWritableValue})`;
    }
    return '';
  }

  private generateForm(): void {
    this.commandForm = new UntypedFormGroup({
      username: new UntypedFormControl({value: '', disabled: true}),
      password: new UntypedFormControl({value: '', disabled: true}),
    });

    this.commandForm.valueChanges.subscribe(_ => {
      this.isSendButtonDisabled = this.commandForm.invalid || this.signalsForm.invalid;
    });

  }

  private unsubscribePolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
  }

  private generateSignalsForm(): void {
    const form: {[key: string]: UntypedFormControl} = {};
    if (!this.chartSettings?.signals) return;
    for (const item of this.chartSettings.signals) {
      const machineSignal = (Object.values(this.machineSignals) as Signal[]).find((machineSignal: Signal) => machineSignal.id == item.signalId);
      if (!machineSignal) {
        continue;
      }
      const validators: ValidatorFn[] = [];
      if (machineSignal.minWritableValue != null) validators.push(Validators.min(machineSignal.minWritableValue));
      if (machineSignal.maxWritableValue != null) validators.push(Validators.max(machineSignal.maxWritableValue));
      form[item.signalName] = new UntypedFormControl({value: '',  disabled: !machineSignal.writable}, validators);
    }
    this.signalsForm = new UntypedFormGroup(form);
    this.signalsForm.valueChanges.subscribe(_ => {
      this.isSendButtonDisabled = this.commandForm.invalid || this.signalsForm.invalid;
    });
  }

  private getMachineSignalById(id: number | string, machineSignals: Signal[]) {
    if (!machineSignals) {
      return undefined;
    }
    return Object.values(machineSignals).find(function (item: Signal) {
      return item.id === id;
    });
  }
  private isReceivedDataValid(data): boolean {
    if (!data || data.length === 0) {
      return false;
    }

    if (!data[0]?.vs || data[0].vs.length === 0) {
      return false;
    }

    return true;
  }
}
